"use client";

import { useState, useRef, useEffect } from 'react';
import { GetUserProfileImage } from '@/app/dashboard/chat-bot/actions';
import { speakResponse } from '@/components/FloatingChat/chatBotUtils';
import ChatBotUi from '@/components/FloatingChat/chatBotUi';
import Link from 'next/link';
import LoginRedirect from '@/components/LoginRedirect';
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";

interface Message {
  role: 'user' | 'agent';
  text: string;
}

export default function FloatingChat() {
  // const [isOpen, setIsOpen] = useState(false);
  const [userLocation, setUserLocation] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isCompLoading, setIsCompLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { user, isLoading, isAuthenticated } = useKindeBrowserClient();

  const toggleListening = () => {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      
      if (!SpeechRecognition) {
      alert("Browser does not support Speech Recognition.");
      return;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.continuous = false;

      if (isListening) {
      recognition.stop();
      return;
      }

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onerror = () => setIsListening(false);

      recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      // Immediately trigger the send logic with the transcript
      handleSend(transcript);
      };

      recognition.start();
  };

  // --- CORE LOGIC ---
  useEffect(() => {
      const fetchUserPhoto = async () => {
        try {
            const photo = await GetUserProfileImage();
            setUserPhoto(photo);
        } catch (error) {
            console.error("Failed to fetch user photo:", error);
        }
      };

      // getCity();
      fetchUserPhoto();

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            // We use a free Reverse Geocoding API to turn coordinates into a Suburb name
            const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
            const data = await res.json();
            
            // This will now capture "Narwee" instead of just "Sydney"
            const specificLocation = `${data.locality || data.city}, ${data.principalSubdivision}`;
            setUserLocation(specificLocation);
          } catch (error) {
            console.error("Reverse geocoding failed", error);
          }
        }, (error) => {
          console.error("User denied location or error occurred", error);
        }, {
          enableHighAccuracy: true // This forces the device to use GPS if available
        });
      }
  }, []);

  useEffect(() => {
      const textarea = textareaRef.current;
      if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
      if (document.activeElement === textarea) {
          textarea.focus({ preventScroll: true });
      }
      }
  }, [input]);

  useEffect(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, isCompLoading]);

  const handleSend = async (overrideInput?: string) => {
      const textToSend = overrideInput || input;
      if (!textToSend.trim() || isCompLoading) return;

      const userMessage: Message = { role: 'user', text: textToSend };
      setMessages((prev) => [...prev, userMessage]);
      setInput('');
      setIsCompLoading(true);

      try {
      const response = await fetch('/api/agent-api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            message: textToSend,
            location: userLocation 
          }),
      });

      const data = await response.json();
      const agentText = data.queryResult?.responseMessages?.[0]?.text?.text?.[0] 
                          || "I'm sorry, I couldn't process that.";

      setMessages((prev) => [...prev, { role: 'agent', text: agentText }]);
      
      // ✅ Speak the reply
      speakResponse(agentText);

      } catch (error) {
      setMessages((prev) => [...prev, { role: 'agent', text: "Error: Could not reach the AI agent." }]);
      } finally {
      setIsCompLoading(false);
      }
  };

  return (
    <>
      <div className="space-y-8 w-full z-30 flex flex-col">
        <div className='text-lg'>
          <Link className='underline' href={"/"}>Home</Link>
          <span className='pl-2 pr-2'>{">"}</span>
          <Link className='underline' href={"/dashboard"}>Dashbord</Link>
          <span className='pl-2 pr-2'>{"> Carta AI Chatbot"}</span>
        </div>
        
        <ChatBotUi 
          isOpen={true} isLoading={isCompLoading} isListening={false} 
          messages={messages} userPhoto={userPhoto ?? ''} input={input} 
          handleSend={handleSend} 
          setIsOpen={() => {}} 
          setInput={setInput} 
          toggleListening={toggleListening} 
          isPage={true}

        />

      </div>

      {/* 2. THE OVERLAY (Only shows if not logged in) */}
      {(!isAuthenticated && !isLoading) && (
        <LoginRedirect message="to analyze this job description" />
      )}
    </>
  );
}